// Eskom PARCC — compliance-terminology reconciliation for the rule book.
//
// The two workbooks spell their compliance levels differently and even swap the
// "Non-Compliant"/"Non-Conformance" head-words between files. We treat the NTCSA
// legend as authoritative (Possible IE = the most severe, PFMA-anchored level —
// consistent with domain/compliance RANK and the Rule Book's decision hierarchy)
// and map the Other-Divisions "DO NOT CHANGE THIS ROW" defaults
// ("possible non-compliance" / "non-conformance") into the register vocabulary.
// Caveat cells ("Depends…", "possible non-compliance if…") yield no firm
// suggestion — the reviewer decides.
import type { ComplianceLevel } from '../enums';

/** The internal canonical default vocabulary (matches enums NEW_COMPLIANCE_LEVELS,
 *  plus the workbook's own "not applicable" default). */
export type CanonicalDefault =
  | 'Possible Non-Compliance'
  | 'Non-Conformance'
  | 'Non-Conformance - Administrative Control Weakness'
  | 'Not Applicable';

/**
 * Normalise a workbook default-compliance cell to the canonical vocabulary
 * (undefined = caveat/none).
 *
 * R6 (Jul 2026): the refreshed non-NTCSA workbook fills the "DO NOT CHANGE THIS
 * ROW" hints on eight sheets that previously carried none, which surfaced two
 * spellings the old folds missed:
 *   • "noncompliance" — written WITHOUT the hyphen, so a literal
 *     `includes('non-compliance')` never fired and the criterion silently lost
 *     its suggestion. Matching is now hyphen-insensitive.
 *   • "not applicable" — a firm answer, not a caveat; it now yields the
 *     register's Not Applicable rather than "reviewer decides".
 * The `Depends…` / `potential…` / `possible non-compliance if…` cells remain
 * deliberately unmapped — those genuinely are the reviewer's call.
 */
export function normalizeDefaultLevel(raw: string | undefined | null): CanonicalDefault | undefined {
  const n = String(raw ?? '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
  if (!n) return undefined;
  // Free-text caveats → no firm suggestion. Tested FIRST, before any fold, so a
  // caveat that happens to name a level ("Depends, i.e DoI will result in
  // possible non-compliance") is never mistaken for that level.
  if (/^depends?\b|^potential|if not in line|^possible non-compliance if|which doc|is \d+ an/.test(n)) {
    return undefined;
  }
  // Strip the "- possible FWE/IE" severity aside before folding, so the head-word
  // is what decides. Done while the hyphens are still present.
  const stripped = n.replace(/[-–]\s*possible (fwe|ie)[^,]*/i, '').trim();
  // Fold hyphens away so "noncompliance", "non compliance" and "non-compliance"
  // are one token. Conformance is tested before compliance: a cell naming both
  // ("non-conformance / noncompliance policies") is a conformance finding.
  const base = stripped.replace(/[-–]/g, ' ').replace(/\s+/g, '');
  if (base.includes('administrative')) return 'Non-Conformance - Administrative Control Weakness';
  if (base.includes('notapplicable')) return 'Not Applicable';
  if (base.includes('possiblenoncompliance')) return 'Possible Non-Compliance';
  if (base.includes('nonconformance')) return 'Non-Conformance';
  if (base.includes('noncompliance')) return 'Non-Conformance';
  return undefined;
}

const REGISTER: Record<CanonicalDefault, ComplianceLevel> = {
  'Possible Non-Compliance': 'Non-Compliance: Possible IE',
  'Non-Conformance': 'Non-Compliant: Policy & Procedures',
  'Non-Conformance - Administrative Control Weakness': 'Non-Compliance Housekeeping',
  'Not Applicable': 'Not Applicable',
};

/** Map a canonical default to the register compliance level the app stores/badges. */
export function registerLevelFor(c: CanonicalDefault): ComplianceLevel {
  return REGISTER[c];
}

// Most-severe-wins ranking (1 = most severe), aligned with domain/compliance.
const RANK: Record<string, number> = {
  'Non-Compliance: Possible IE': 1,
  'Non-Compliant: Policy & Procedures': 2,
  'Non-Compliance Housekeeping': 3,
};

/** Return the more severe of two register compliance levels. */
export function moreSevere(a: ComplianceLevel, b: ComplianceLevel): ComplianceLevel {
  return (RANK[a] ?? 99) <= (RANK[b] ?? 99) ? a : b;
}
